'use client';

import { Routes } from '@/app/routes';
import { cn } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { SYNTHESIZE_CHANNELS } from '../explore-channels';

/** Ported verbatim from the design source of record (`MFour Studio JTBD
 * v15.dc.html`, `comboLenses`) — who the combined channels should cover. */
const LENSES = [
    'My survey respondents',
    'My verified buyers',
    'My target audience',
    'All verified consumers',
];

interface SynthesizeChannelsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * The 3-step "Synthesize data" wizard from the design source of record
 * (`MFour Studio JTBD v15.dc.html`, the `comboOpen` modal): pick channels,
 * pick who it should cover, then confirm a summary and hand it to Ask DANI.
 * A true centered modal (not a rail hover-peek) — matches the mockup's own
 * treatment of this flow as a distinct, focused task.
 */
export function SynthesizeChannelsModal({ isOpen, onClose }: SynthesizeChannelsModalProps) {
    const router = useRouter();
    const [step, setStep] = useState(1);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [lens, setLens] = useState<string | null>(null);

    if (!isOpen) return null;

    const reset = () => {
        setStep(1);
        setSelectedKeys([]);
        setLens(null);
    };

    const handleClose = () => {
        onClose();
        reset();
    };

    const toggleChannel = (key: string) => {
        setSelectedKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
        );
    };

    const selectedNames = SYNTHESIZE_CHANNELS.filter((c) => selectedKeys.includes(c.key)).map(
        (c) => c.name,
    );
    const lensLabel = (lens ?? 'your audience').toLowerCase();
    const summary = `Combine ${selectedNames.join(', ') || 'your channels'} for ${lensLabel} and surface what stands out.`;

    const canContinue = step === 1 ? selectedKeys.length > 0 : step !== 2 || !!lens;

    const handleAsk = () => {
        const prompt = `Combine ${selectedNames.join(', ')} for ${lensLabel} and show me what stands out`;
        router.push(`${Routes.STUDIO_AI}?seed=${encodeURIComponent(prompt)}`);
        handleClose();
    };

    return (
        <>
            <div className="fixed inset-0 z-[70] bg-[#192138]/[0.55]" onClick={handleClose} />
            <div className="fixed left-1/2 top-1/2 z-[71] w-[340px] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-divider bg-background p-4 text-left shadow-2xl">
                <div className="mb-3 flex items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#22CD89]/[0.14] text-[#159c67]">
                        <Icon icon="lucide:infinity" width={14} height={14} />
                    </span>
                    <span className="flex-1 text-[13px] font-bold tracking-tight text-foreground">
                        Synthesize data
                    </span>
                    <span className="whitespace-nowrap text-[10.5px] text-default-400">
                        Step {step} of 3
                    </span>
                    <button
                        type="button"
                        onClick={handleClose}
                        aria-label="Close"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-default-400 hover:bg-default-100"
                    >
                        <Icon icon="lucide:x" width={13} height={13} />
                    </button>
                </div>

                {step === 1 && (
                    <div className="flex flex-wrap gap-1.5">
                        {SYNTHESIZE_CHANNELS.map((channel) => {
                            const isSelected = selectedKeys.includes(channel.key);
                            return (
                                <button
                                    key={channel.key}
                                    type="button"
                                    onClick={() => toggleChannel(channel.key)}
                                    className="inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11.5px] font-medium transition-colors"
                                    style={{
                                        borderColor: isSelected ? channel.color : undefined,
                                        background: isSelected ? `${channel.color}18` : undefined,
                                        color: isSelected ? channel.color : undefined,
                                    }}
                                >
                                    <Icon icon={`lucide:${channel.icon}`} width={12} height={12} />
                                    {channel.short}
                                </button>
                            );
                        })}
                    </div>
                )}

                {step === 2 && (
                    <>
                        <p className="mb-2.5 text-[11.5px] text-default-500">
                            Who should this cover?
                        </p>
                        <div className="flex flex-col gap-1">
                            {LENSES.map((option) => {
                                const isSelected = lens === option;
                                return (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={() => setLens(option)}
                                        className={cn(
                                            'flex items-center gap-2 rounded-large border px-2.5 py-2 text-left text-[12.5px] font-medium text-foreground transition-colors',
                                            isSelected
                                                ? 'border-primary bg-primary-50'
                                                : 'border-divider hover:bg-default-100',
                                        )}
                                    >
                                        <span className="min-w-0 flex-1">{option}</span>
                                        {isSelected && (
                                            <Icon
                                                icon="lucide:check"
                                                width={13}
                                                height={13}
                                                className="shrink-0 text-primary"
                                            />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}

                {step === 3 && (
                    <p className="rounded-large border border-divider bg-default-50 p-2.5 text-[12.5px] leading-relaxed text-foreground">
                        {summary}
                    </p>
                )}

                <div className="mt-3 flex items-center justify-between">
                    {step > 1 ? (
                        <button
                            type="button"
                            onClick={() => setStep((s) => s - 1)}
                            className="h-[30px] rounded-large border border-divider px-3 text-[12px] font-medium text-default-700 hover:bg-default-100"
                        >
                            Back
                        </button>
                    ) : (
                        <span />
                    )}
                    {step < 3 ? (
                        <button
                            type="button"
                            disabled={!canContinue}
                            onClick={() => setStep((s) => s + 1)}
                            className="h-[30px] rounded-large bg-foreground px-3.5 text-[12px] font-semibold text-background disabled:opacity-40"
                        >
                            Continue
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handleAsk}
                            className="flex h-[30px] items-center gap-1.5 rounded-large bg-[#22CD89] px-3.5 text-[12px] font-semibold text-[#0b3f29]"
                        >
                            <Icon icon="lucide:sparkles" width={13} height={13} />
                            Ask DANI
                        </button>
                    )}
                </div>
            </div>
        </>
    );
}

SynthesizeChannelsModal.displayName = 'SynthesizeChannelsModal';
